import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PackagePlus, Pencil } from "lucide-react";

import { medicalService, type Medication } from "../../services/medicalService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { errorLine } from "./ClinicParts";
import { MEDICATION_TYPES, STOCK_LABELS, STOCK_MOVES, day, labelFrom, medicineConfig, medicineName, medicineStatus, signedQuantity } from "./medicalLogic";

const money = (value?: string | null) => (value ? `UGX ${Number(value).toLocaleString()}` : undefined);

function MedicineForm({ medicine, onClose, onDone }: { medicine?: Medication; onClose: () => void; onDone: (medicine: Medication) => void }) {
  const [prescriptionOnly, setPrescriptionOnly] = useState(medicine?.requires_prescription ?? true);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (medicine ? medicalService.updateMedication({ id: medicine.id, data }) : medicalService.createMedication(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const data: Record<string, unknown> = {
      name: text("name"),
      generic_name: text("generic_name") || null,
      medication_type: text("medication_type"),
      dosage_form: text("dosage_form") || null,
      strength: text("strength") || null,
      minimum_stock: Number(text("minimum_stock") || 0),
      unit_price: text("unit_price") || null,
      expiry_date: text("expiry_date") || null,
      requires_prescription: prescriptionOnly,
      indications: text("indications") || null,
      contraindications: text("contraindications") || null,
      side_effects: text("side_effects") || null,
      dosage_instructions: text("dosage_instructions") || null,
      storage_conditions: text("storage_conditions") || null,
      notes: text("notes") || null,
    };
    // Stock changes only through recorded movements once the medicine exists.
    if (!medicine) Object.assign(data, { current_stock: Number(text("current_stock") || 0), is_active: true });
    save.mutate(data);
  };
  const value = (name: keyof Medication) => (medicine?.[name] as string | number | null | undefined) ?? "";

  return (
    <FormModal
      id="medicine"
      title={medicine ? `Edit ${medicineName(medicine)}` : "Add a medicine to the store"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The medicine could not be saved.") : undefined}
      saveLabel={medicine ? "Save changes" : "Add the medicine"}
      note={medicine ? "Change the count with a stock movement, so it stays audited." : undefined}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The medicine" divided={false}>
        <Field label="Name" hint="As on the pack, e.g. Panadol.">
          <input required name="name" maxLength={200} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="Generic name" optional hint="e.g. paracetamol.">
          <input name="generic_name" maxLength={200} defaultValue={value("generic_name")} className="input mt-1" />
        </Field>
        <Field label="Form">
          <select name="medication_type" defaultValue={value("medication_type") || "tablet"} className="input mt-1">
            {MEDICATION_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Strength" optional hint="e.g. 500 mg, 125 mg/5 ml.">
          <input name="strength" maxLength={100} defaultValue={value("strength")} className="input mt-1" />
        </Field>
        <Field label="Pack" optional hint="e.g. strip of 10, 100 ml bottle.">
          <input name="dosage_form" maxLength={100} defaultValue={value("dosage_form")} className="input mt-1" />
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={prescriptionOnly} onChange={(event) => setPrescriptionOnly(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Given only on a clinician's prescription
        </label>
      </FormSection>

      <FormSection title="Stock" columns={medicine ? 3 : 2}>
        {!medicine && (
          <Field label="Opening stock" hint="Counted today.">
            <input required type="number" name="current_stock" min={0} step={1} defaultValue={0} className="input mt-1 tabular-nums" />
          </Field>
        )}
        <Field label="Reorder at" hint="Flagged when stock falls to this.">
          <input required type="number" name="minimum_stock" min={0} step={1} defaultValue={value("minimum_stock") || 10} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Unit price (UGX)" optional>
          <input type="number" name="unit_price" min={0} step="any" defaultValue={value("unit_price")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Expiry date" optional hint="Of the batch on the shelf.">
          <input type="date" name="expiry_date" defaultValue={value("expiry_date")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="For the clinician" description="Shown when the medicine is prescribed.">
        <Field label="Usual dose" optional className="sm:col-span-2" hint="e.g. children 6–12 years: 250–500 mg every 6 hours, at most 4 doses a day.">
          <textarea name="dosage_instructions" rows={2} defaultValue={value("dosage_instructions")} className="input mt-1" />
        </Field>
        <Field label="Used for" optional>
          <textarea name="indications" rows={2} defaultValue={value("indications")} className="input mt-1" />
        </Field>
        <Field label="Not to be given for" optional hint="Warned about when prescribing.">
          <textarea name="contraindications" rows={2} defaultValue={value("contraindications")} className="input mt-1" />
        </Field>
        <Field label="Side effects" optional>
          <textarea name="side_effects" rows={2} defaultValue={value("side_effects")} className="input mt-1" />
        </Field>
        <Field label="Storage" optional hint="e.g. below 25 °C, keep in the fridge.">
          <textarea name="storage_conditions" rows={2} defaultValue={value("storage_conditions")} className="input mt-1" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} defaultValue={value("notes")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** What the sick bay holds, what is running low or going out of date, and every movement of stock. */
export function MedicinesPanel({ medicines, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "follow-up" from the dashboard. */
  initialView?: string;
  medicines?: Medication[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Medication | "new" | null>(null);
  const [move, setMove] = useState("return");
  const [quantity, setQuantity] = useState("");
  const [moves, setMoves] = useState(0);
  const history = useQuery({
    queryKey: ["medical-stock-history", viewing],
    queryFn: () => medicalService.getStockHistory(viewing!),
    enabled: viewing !== null,
  });
  const adjust = useMutation({
    mutationFn: medicalService.adjustMedicationStock,
    onSuccess: (entry) => {
      setQuantity("");
      setMoves((count) => count + 1);
      const label = (STOCK_LABELS[entry.transaction_type] ?? entry.transaction_type).toLowerCase();
      setNotice({ tone: "success", text: `${entry.medication_name}: ${entry.quantity > 0 ? "+" : ""}${entry.quantity} (${label}). ${entry.stock_after} now in the store.` });
      history.refetch();
      onChanged();
    },
  });

  const columns: RegisterColumn<Medication>[] = [
    {
      header: "Medicine",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{medicineName(row)}</span>
          {row.generic_name && <span className="block text-xs text-gray-500">{row.generic_name}</span>}
        </span>
      ),
      exportValue: (row) => medicineName(row),
      className: "min-w-[12rem]",
    },
    { header: "Generic name", cell: (row) => row.generic_name ?? "", exportValue: (row) => row.generic_name ?? "", exportOnly: true },
    { header: "Form", cell: (row) => labelFrom(MEDICATION_TYPES, row.medication_type), exportValue: (row) => labelFrom(MEDICATION_TYPES, row.medication_type), className: "text-gray-700" },
    { header: "In stock", cell: (row) => <span className={row.current_stock <= row.minimum_stock ? "font-semibold text-red-700" : "font-medium"}>{row.current_stock}</span>, exportValue: (row) => row.current_stock, align: "right" },
    { header: "Reorder at", cell: (row) => row.minimum_stock, exportValue: (row) => row.minimum_stock, align: "right", className: "text-gray-600" },
    { header: "Expires", cell: (row) => day(row.expiry_date) || <span className="text-gray-300">—</span>, exportValue: (row) => day(row.expiry_date), className: "tabular-nums text-gray-700" },
    { header: "Prescription", cell: (row) => (row.requires_prescription ? "Needed" : "Not needed"), exportValue: (row) => (row.requires_prescription ? "Needed" : "Not needed"), className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...medicineStatus(row, today)} />, exportValue: (row) => medicineStatus(row, today).label },
  ];

  const medicine = viewing !== null ? medicines?.find((row) => row.id === viewing) : undefined;
  const chosen = STOCK_MOVES.find((entry) => entry.value === move)!;
  const change = quantity && Number(quantity) ? signedQuantity(move, Number(quantity)) : 0;
  const after = medicine ? medicine.current_stock + change : 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Medicine store"
        description={canManage ? "Open a medicine to record stock coming in or going out, see its history or edit it." : "What the sick bay holds."}
        actions={canManage && (
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <PackagePlus className="mr-1.5 h-3.5 w-3.5" /> Add a medicine
          </button>
        )}
        rows={medicines}
        config={medicineConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          adjust.reset();
          setMove("return");
          setQuantity("");
          setViewing(row.id);
        }}
        openLabel={() => (canManage ? "Stock / edit" : "Open")}
        noun={["medicine", "medicines"]}
        exportName={{ file: "sick-bay-medicines", title: "Sick Bay Medicine Store" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "The store is empty. Add each medicine with its opening count." : "No medicines recorded."}
      />

      {medicine && !editing && (
        <DetailsModal
          size="3xl"
          title={medicineName(medicine)}
          headline={{ label: "In the store", value: String(medicine.current_stock), note: `Reorder at ${medicine.minimum_stock}` }}
          status={medicineStatus(medicine, today)}
          facts={[
            ["Generic name", medicine.generic_name ?? undefined],
            ["Form", labelFrom(MEDICATION_TYPES, medicine.medication_type)],
            ["Strength", medicine.strength ?? undefined],
            ["Pack", medicine.dosage_form ?? undefined],
            ["Expires", day(medicine.expiry_date) || undefined],
            ["Unit price", money(medicine.unit_price)],
            ["Prescription", medicine.requires_prescription ? "Needed" : "Not needed"],
          ]}
          sections={[
            { title: "Usual dose", body: medicine.dosage_instructions },
            { title: "Used for", body: medicine.indications },
            { title: "Not to be given for", body: medicine.contraindications, tone: "warning" },
            { title: "Side effects", body: medicine.side_effects },
            { title: "Storage", body: medicine.storage_conditions },
            { title: "Notes", body: medicine.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(medicine)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          {canManage && (
            <ActionPanel title="Record a stock movement" description="Stock given out on prescriptions is taken off when it is dispensed; record everything else here.">
              <form
                key={moves}
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  adjust.mutate({
                    id: medicine.id,
                    transaction_type: move,
                    quantity: Number(quantity),
                    reference: String(data.get("reference") ?? "").trim(),
                    notes: String(data.get("notes") ?? "").trim(),
                  });
                }}
                className="grid gap-3 sm:grid-cols-2"
              >
                <Field label="Movement" hint={chosen.hint}>
                  <select value={move} onChange={(event) => setMove(event.target.value)} className="input mt-1">
                    {STOCK_MOVES.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
                  </select>
                </Field>
                <Field
                  label="Quantity"
                  hint={change ? <span className={after < 0 ? "text-red-700" : undefined}>{medicine.current_stock} → {after} in the store{after < 0 ? ": more than there is" : ""}</span> : "Tablets, bottles or tubes, as counted."}
                >
                  <input required type="number" step={1} min={chosen.sign === 0 ? undefined : 1} value={quantity} onChange={(event) => setQuantity(event.target.value)} className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Reference" optional hint="e.g. delivery note or NMS order number.">
                  <input name="reference" maxLength={100} className="input mt-1" />
                </Field>
                <Field label="Notes" optional>
                  <input name="notes" className="input mt-1" />
                </Field>
                <div className="flex justify-end sm:col-span-2">
                  <button disabled={adjust.isPending || !change || after < 0} className="btn btn-primary">{adjust.isPending ? "Saving…" : "Record the movement"}</button>
                </div>
              </form>
              {adjust.isError && errorLine(adjust.error, "The stock movement could not be recorded.")}
            </ActionPanel>
          )}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stock history</h4>
            {history.isLoading ? (
              <div className="mt-1.5 h-16 animate-pulse rounded bg-gray-100" />
            ) : history.isError ? (
              errorLine(history.error, "The stock history could not be loaded.")
            ) : !history.data?.length ? (
              <p className="mt-1.5 text-sm text-gray-500">No movements recorded.</p>
            ) : (
              <div className="sheet-wrap mt-1.5 max-h-72 overflow-y-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Movement</th>
                      <th className="text-right">Change</th>
                      <th className="text-right">After</th>
                      <th>Reference / notes</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.data.map((entry) => (
                      <tr key={entry.id}>
                        <td className="whitespace-nowrap tabular-nums">{day(entry.created_at)}</td>
                        <td>{STOCK_LABELS[entry.transaction_type] ?? entry.transaction_type}</td>
                        <td className={`text-right tabular-nums ${entry.quantity < 0 ? "text-red-700" : "text-emerald-700"}`}>{entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}</td>
                        <td className="text-right tabular-nums">{entry.stock_after}</td>
                        <td className="min-w-[10rem] whitespace-normal text-gray-700">{[entry.reference, entry.notes].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="text-gray-700">{entry.processed_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <MedicineForm
          medicine={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${medicineName(saved)} was added with ${saved.current_stock} in stock.` : `${medicineName(saved)} was updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
